import { useEffect, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  onlineManager,
} from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@servwell/api/router';
import {
  formatQty,
  restockBatchSendInput,
  reorderDraftsInput,
  type ReorderPlanInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { sameAccount, type AccountRef } from '../../lib/account';
import { identity } from '../../lib/identity';
import {
  readReorderWrite,
  reserveReorderWrite,
  acceptReorderWrite,
  clearReorderWrite,
  clearUnreadableReorderWrite,
  type ReorderWriteAttempt,
  type ReorderWriteRead,
} from '../../lib/reorder-write-recovery';
import { reorderWriteInvalidatesQuery } from '../../lib/restock-query-policy';
import { isRetryableMutationRefusal, refusalStatus } from '../../lib/refusal';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Field, field } from '../create/Field';
import { Notice } from '../ui/Badge';
import {
  shownState,
  UnresolvedState,
  refreshFailed,
  StaleNotice,
} from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { money } from '../money';

export type ReorderPlan = inferRouterOutputs<AppRouter>['restocks']['reorderPlan'];
export type ReorderReview = { action: 'send' | 'draft'; input: ReorderPlanInput };
export const quantityInput = (n: number) => String(n / 100);
function hundredths(text: string) {
  if (!/^\d+(?:\.\d{0,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.'),
    n = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(n) ? n : null;
}
const active = (owner: AccountRef) => {
  const s = identity.cacheAccount.snapshot();
  return s.status === 'settled' && sameAccount(s.account, owner);
};
export function ReorderPlanSheet({
  owner,
  review,
  saved,
  onClose,
  onStorageChange,
}: {
  owner: AccountRef;
  review?: ReorderReview;
  saved: ReorderWriteRead;
  onClose: () => void;
  onStorageChange: () => void;
}) {
  const trpc = useTRPC(),
    qc = useQueryClient();
  const [input, setInput] = useState<ReorderPlanInput>(
      review?.input ?? { acknowledgeNegative: false },
    ),
    [edits, setEdits] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState<ReorderWriteAttempt | null>(
    saved.status === 'ready' ? saved.attempt : null,
  );
  const [unreadable, setUnreadable] = useState(saved.status === 'unreadable');
  const [retained, setRetained] = useState<ReorderWriteAttempt | null>(null);
  const action = attempt?.action ?? retained?.action ?? review?.action;
  const [message, setMessage] = useState<string | null>(null),
    [claiming, setClaiming] = useState(false),
    [confirmedClear, setConfirmedClear] = useState(false);
  const gate = useRef(false),
    keys = useRef<ReturnType<typeof idem> | null>(null);
  const [online, setOnline] = useState(onlineManager.isOnline());
  useEffect(() => onlineManager.subscribe(setOnline), []);
  const plan = useQuery(
    trpc.restocks.reorderPlan.queryOptions(input, {
      enabled: !attempt && !unreadable && online,
      staleTime: 0,
    }),
  );
  const data = plan.data;
  const syncStorage = () => {
    const latest = readReorderWrite(owner);
    setUnreadable(latest.status === 'unreadable');
    if (latest.status === 'ready')
      setAttempt((previous) => {
        if (
          previous &&
          previous.request.mutationId !== latest.attempt.request.mutationId
        ) {
          setMessage(
            'Another saved request replaced this slot. The original request remains shown; retry only its exact key, and resolve the replacement separately.',
          );
          return previous;
        }
        return previous?.accepted
          ? ({
              ...latest.attempt,
              accepted: true,
              receipt: previous.receipt,
            } as ReorderWriteAttempt)
          : latest.attempt;
      });
    onStorageChange();
  };
  useEffect(() => {
    const listen = () => syncStorage();
    window.addEventListener('storage', listen);
    return () => window.removeEventListener('storage', listen);
  }, [owner.orgId, owner.userId]);
  const accepted = async (a: ReorderWriteAttempt, result: unknown) => {
    // Response -> refresh this owner's cache -> exact-key receipt. Never touch a newer claim.
    const receipt =
      a.action === 'send'
        ? { restocks: (result as any).restocks }
        : { created: (result as any).created };
    const confirmed = { ...a, accepted: true, receipt } as ReorderWriteAttempt;
    setAttempt(confirmed);
    if (active(owner))
      await qc.invalidateQueries({
        predicate: (q) => reorderWriteInvalidatesQuery(q.queryKey[0]),
      });
    if (!(await acceptReorderWrite(owner, a, result)))
      setMessage(
        'Accepted. This device could not save its receipt, or another request replaced the saved slot. Keep these numbers and verify before clearing.',
      );
    else setMessage(null);
    onStorageChange();
  };
  const refused = async (error: unknown, a: ReorderWriteAttempt) => {
    setMessage(
      error instanceof Error
        ? error.message
        : 'The outcome is unknown. Keep this request and retry the same key.',
    );
    if (a.accepted || refusalStatus(error) === 403 || isRetryableMutationRefusal(error)) {
      setAttempt(a);
      syncStorage();
      return;
    }
    // A definitive unaccepted refusal can be reviewed again with retained values; uncertain/accepted keys stay parked.
    if (await clearReorderWrite(owner, a.request.mutationId)) {
      keys.current = null;
      setRetained(a);
      setAttempt(null);
      setInput({
        feederIds: a.request.feederIds,
        sends: a.request.sends,
        buyItems: a.request.buyItems?.map(({ feederId, itemId }) => ({
          feederId,
          itemId,
        })),
        acknowledgeNegative: a.request.acknowledgeNegative,
      });
      onStorageChange();
    } else syncStorage();
  };
  const send = useMutation(
    trpc.restocks.sendBatch.mutationOptions({
      networkMode: 'always',
      retry: false,
      onSettled: () => {
        gate.current = false;
        setClaiming(false);
      },
      onSuccess: (r, v) =>
        accepted(
          {
            action: 'send',
            request: restockBatchSendInput.parse(v),
            ...(attempt?.action === 'send'
              ? { labels: attempt.labels, accepted: attempt.accepted }
              : {}),
          },
          r,
        ),
      onError: (e, v) =>
        refused(e, {
          action: 'send',
          request: restockBatchSendInput.parse(v),
          ...(attempt?.action === 'send'
            ? { labels: attempt.labels, accepted: attempt.accepted }
            : {}),
        }),
    }),
  );
  const draft = useMutation(
    trpc.purchasing.createReorderDrafts.mutationOptions({
      networkMode: 'always',
      retry: false,
      onSettled: () => {
        gate.current = false;
        setClaiming(false);
      },
      onSuccess: (r, v) =>
        accepted(
          {
            action: 'draft',
            request: reorderDraftsInput.parse(v),
            ...(attempt?.action === 'draft'
              ? { labels: attempt.labels, accepted: attempt.accepted }
              : {}),
          },
          r,
        ),
      onError: (e, v) =>
        refused(e, {
          action: 'draft',
          request: reorderDraftsInput.parse(v),
          ...(attempt?.action === 'draft'
            ? { labels: attempt.labels, accepted: attempt.accepted }
            : {}),
        }),
    }),
  );
  const settle = () => {
    gate.current = false;
    setClaiming(false);
  };
  const pending = claiming || send.isPending || draft.isPending;
  const deliver = async (candidate: ReorderWriteAttempt) => {
    if (gate.current || !online || !active(owner)) return;
    gate.current = true;
    setClaiming(true);
    let delivered = false;
    try {
      if (!(await reserveReorderWrite(owner, candidate))) {
        setMessage(
          'Another saved inventory request, unavailable browser coordination, or unreadable/full storage prevents delivery. Resolve the saved request before sending again.',
        );
        syncStorage();
        return;
      }
      setAttempt(candidate);
      onStorageChange();
      if (!active(owner)) return; // Account changed while waiting: park the original owner's key.
      delivered = true;
      if (candidate.action === 'send') send.mutate(candidate.request);
      else draft.mutate(candidate.request);
    } finally {
      if (!delivered) settle();
    }
  };
  const submit = () => {
    if (
      !data ||
      !action ||
      plan.isFetching ||
      plan.isError ||
      Object.keys(edits).length ||
      pending
    )
      return;
    try {
      const key = keys.current ?? (keys.current = idem());
      const request = { ...data.proposal, expectedPlanRevision: data.revision, ...key };
      const labels = {
        vans: data.vans.map((v) => ({
          vanId: v.vanId,
          name: v.vanName,
          items: v.items.map((i) => ({ itemId: i.itemId, name: i.name })),
        })),
        buys: data.buys
          .filter((b) => b.selected)
          .map((b) => ({
            feederId: b.feederId,
            itemId: b.itemId,
            name: b.name,
            destinationName: b.destinationName,
          })),
      };
      void deliver(
        action === 'send'
          ? { action: 'send', request, labels }
          : {
              action: 'draft',
              request: request as Extract<
                ReorderWriteAttempt,
                { action: 'draft' }
              >['request'],
              labels,
            },
      );
    } catch {
      setMessage(
        'This device could not create a saved request. Restore browser storage before continuing.',
      );
    }
  };
  const reviewEdits = () => {
    if (!data) return;
    const sends = data.proposal.sends.map((v) => ({
      ...v,
      items: v.items.map((i) => ({
        ...i,
        sendHundredths: hundredths(
          edits[`${v.vanId}|${i.itemId}`] ?? quantityInput(i.sendHundredths),
        ),
      })),
    }));
    if (sends.some((v) => v.items.some((i) => i.sendHundredths === null))) {
      setMessage('Use a nonnegative quantity with at most two decimal places.');
      return;
    }
    setInput({ ...input, sends: sends as NonNullable<ReorderPlanInput['sends']> });
    setEdits({});
    setMessage(null);
    keys.current = null;
  };
  const rebuildReview = async () => {
    if (!retained || pending || !online || !active(owner)) return;
    setClaiming(true);
    try {
      const base = await qc.fetchQuery(
        trpc.restocks.reorderPlan.queryOptions(
          {
            feederIds: retained.request.feederIds,
            acknowledgeNegative: retained.request.acknowledgeNegative,
          },
          { staleTime: 0 },
        ),
      );
      // Reconcile selection identity only. The server still owns every quantity and allocation.
      const sends = retained.request.sends.flatMap((v) => {
        const current = base.vans.find((c) => c.vanId === v.vanId && c.feederId);
        return current
          ? [
              {
                ...v,
                items: v.items.filter((i) =>
                  current.items.some((c) => c.itemId === i.itemId && c.sendable),
                ),
              },
            ]
          : [];
      });
      const scope = {
        feederIds: retained.request.feederIds,
        sends,
        acknowledgeNegative: retained.request.acknowledgeNegative,
      };
      const current = await qc.fetchQuery(
        trpc.restocks.reorderPlan.queryOptions(scope, { staleTime: 0 }),
      );
      const buyItems = (retained.request.buyItems ?? [])
        .filter((b) =>
          current.buys.some(
            (c) => c.feederId === b.feederId && c.itemId === b.itemId && c.orderable,
          ),
        )
        .map(({ feederId, itemId }) => ({ feederId, itemId }));
      if (!active(owner)) return;
      setInput({ ...scope, buyItems });
      setEdits({});
      keys.current = null;
      setMessage(
        'The previous selections were checked against the current server plan. Unavailable rows were removed from this review; no new Buy suggestions were selected. Review the remaining quantities before submitting.',
      );
    } catch (error) {
      if (active(owner))
        setMessage(
          error instanceof Error
            ? error.message
            : 'Current setup could not be reviewed. The previous quantities remain below.',
        );
    } finally {
      setClaiming(false);
    }
  };
  const clear = async () => {
    if (pending || !active(owner)) return;
    const ok = unreadable
      ? await clearUnreadableReorderWrite(owner)
      : attempt
        ? await clearReorderWrite(owner, attempt.request.mutationId)
        : true;
    if (!ok) {
      setMessage(
        'This device could not clear the saved request, or another request replaced it. The saved key is retained.',
      );
      syncStorage();
      return;
    }
    onStorageChange();
    onClose();
  };
  const phase = shownState(plan, plan.data);
  const valid =
    !!data &&
    !plan.isFetching &&
    !plan.isError &&
    !refreshFailed(plan, plan.data) &&
    !Object.keys(edits).length;
  const requiresAck = data?.requiresAcknowledgement && !input.acknowledgeNegative;
  return (
    <Sheet
      open
      onClose={() => {
        if (!pending) onClose();
      }}
      title={
        attempt
          ? attempt.accepted
            ? 'Inventory request accepted'
            : 'Recover saved inventory request'
          : action === 'send'
            ? 'Review Send'
            : 'Review Draft orders'
      }
      wide
      footer={
        <div className="space-y-3" aria-live="polite">
          {!online ? (
            <Notice tone="warn">
              Office Send and Draft require a connection. This request stays here; it is
              not queued offline.
            </Notice>
          ) : null}
          {message ? <Notice tone="warn">{message}</Notice> : null}
          {retained && !attempt ? (
            <Button
              className="min-h-11 w-full"
              variant="ghost"
              disabled={pending || !online}
              onClick={() => void rebuildReview()}
            >
              Reconcile previous selections with current plan
            </Button>
          ) : null}
          {unreadable ? (
            <>
              <Notice tone="warn">
                The saved inventory request is unreadable. It may already have been
                accepted. An inventory manager must verify its outcome before clearing it.
              </Notice>
              <Field label="An inventory manager verified the saved request outcome">
                <input
                  className="min-h-11 min-w-11"
                  type="checkbox"
                  checked={confirmedClear}
                  onChange={(e) => setConfirmedClear(e.target.checked)}
                />
              </Field>
              <Button
                className="min-h-11 w-full"
                disabled={!confirmedClear || pending}
                onClick={() => void clear()}
              >
                Clear verified unreadable request
              </Button>
            </>
          ) : attempt ? (
            <>
              {!attempt.accepted ? (
                <>
                  <p className="text-sm">
                    The outcome may be unknown. Retry this exact saved request; do not
                    create another key.
                  </p>
                  <Button
                    className="min-h-11 w-full"
                    disabled={pending || !online}
                    onClick={() => void deliver(attempt)}
                  >
                    {pending ? 'Checking saved request…' : 'Retry the same saved request'}
                  </Button>
                  <Field label="An inventory manager confirmed no restock or order was created">
                    <input
                      className="min-h-11 min-w-11"
                      type="checkbox"
                      checked={confirmedClear}
                      onChange={(e) => setConfirmedClear(e.target.checked)}
                    />
                  </Field>
                </>
              ) : null}
              <Button
                className="min-h-11 w-full"
                variant="ghost"
                disabled={pending || (!attempt.accepted && !confirmedClear)}
                onClick={() => void clear()}
              >
                {attempt.accepted
                  ? 'Clear accepted receipt'
                  : 'Discard verified unaccepted request'}
              </Button>
            </>
          ) : (
            <>
              <Button
                className="min-h-11 w-full"
                disabled={
                  !online ||
                  !valid ||
                  !!requiresAck ||
                  pending ||
                  (action === 'draft'
                    ? !data?.proposal.buyItems.length
                    : !data?.selectedSendCount)
                }
                onClick={submit}
              >
                {pending
                  ? 'Saving…'
                  : action === 'send'
                    ? `Send ${data?.selectedSendCount ?? 0} restocks`
                    : `Draft ${data?.draftGroupCount ?? 0} orders`}
              </Button>
              {Object.keys(edits).length ? (
                <Button className="min-h-11 w-full" onClick={reviewEdits}>
                  Review edited quantities
                </Button>
              ) : null}
            </>
          )}
        </div>
      }
    >
      <StaleNotice
        show={refreshFailed(plan, plan.data)}
        onRetry={() => void plan.refetch()}
      />
      {retained && !attempt ? (
        <details className="mb-4 text-sm">
          <summary className="min-h-11 cursor-pointer">
            Previous refused quantities (kept for comparison)
          </summary>
          {retained.request.sends.map((v) => (
            <p key={v.vanId}>
              Van{' '}
              {retained.labels?.vans.find((l) => l.vanId === v.vanId)?.name ?? v.vanId}:{' '}
              {v.items
                .map(
                  (i) =>
                    `${retained.labels?.vans.find((l) => l.vanId === v.vanId)?.items.find((l) => l.itemId === i.itemId)?.name ?? i.itemId}: ${formatQty(i.sendHundredths)}`,
                )
                .join(', ')}
            </p>
          ))}
          {retained.request.buyItems?.map((b) => (
            <p key={`${b.feederId}|${b.itemId}`}>
              {retained.labels?.buys.find(
                (l) => l.feederId === b.feederId && l.itemId === b.itemId,
              )?.name ?? b.itemId}
              : {formatQty(b.quantityHundredths ?? 0)}
            </p>
          ))}
        </details>
      ) : null}
      {attempt ? (
        <div className="space-y-4">
          <p className="text-sm">
            Saved {attempt.action === 'send' ? 'Send' : 'Draft'} ·{' '}
            {attempt.request.mutationId}
          </p>
          {attempt.action === 'send'
            ? attempt.request.sends.map((v) => (
                <section key={v.vanId}>
                  <h3 className="font-semibold">
                    {attempt.labels?.vans.find((l) => l.vanId === v.vanId)?.name ??
                      `Van ${v.vanId}`}
                  </h3>
                  {v.items.map((i) => (
                    <p className="text-sm" key={i.itemId}>
                      {attempt.labels?.vans
                        .find((l) => l.vanId === v.vanId)
                        ?.items.find((l) => l.itemId === i.itemId)?.name ??
                        `Part ${i.itemId}`}
                      : {formatQty(i.sendHundredths)}
                    </p>
                  ))}
                </section>
              ))
            : attempt.request.buyItems.map((b) => (
                <p key={`${b.feederId}|${b.itemId}`}>
                  {attempt.labels?.buys.find(
                    (l) => l.feederId === b.feederId && l.itemId === b.itemId,
                  )?.name ?? `Part ${b.itemId}`}{' '}
                  · {formatQty(b.quantityHundredths)} to{' '}
                  {attempt.labels?.buys.find(
                    (l) => l.feederId === b.feederId && l.itemId === b.itemId,
                  )?.destinationName ?? b.feederId}
                </p>
              ))}
          <p className="text-sm">
            Negative-root acknowledgement:{' '}
            {attempt.request.acknowledgeNegative ? 'Yes' : 'No'}
          </p>
          {attempt.accepted ? (
            <div role="status" className="space-y-2">
              <h3 className="font-semibold">Accepted receipts</h3>
              {attempt.action === 'send'
                ? attempt.receipt?.restocks.map((r) => (
                    <p key={r.restockId}>R-{r.restockNumber}</p>
                  ))
                : attempt.receipt?.created.map((r) => (
                    <p key={r.id}>PO-{r.poNumber} (draft, not sent)</p>
                  ))}
              {attempt.action === 'send' && attempt.receipt?.restocks.length === 0 ? (
                <p>No stock sent. No restock was created.</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : unreadable ? null : phase !== 'ready' ? (
        <UnresolvedState
          state={phase}
          what="reviewed Reorder quantities"
          icon={IconInventory}
          error={plan.error}
          onRetry={() => void plan.refetch()}
        />
      ) : data ? (
        <div className="space-y-5">
          <p className="text-sm">
            This review uses the selected feeders and all competing van demand. Changes to
            stock or setup require another review.
          </p>
          {action === 'send' ? (
            data.vans
              .filter((v) => data.proposal.sends.some((s) => s.vanId === v.vanId))
              .map((v) => (
                <section key={v.vanId} className="space-y-3">
                  <h3 className="font-semibold">
                    {v.vanName} from {v.feederName}
                  </h3>
                  {v.items.map((i) => (
                    <div key={i.itemId} className="space-y-1">
                      <Field label={`Send ${i.name} to ${v.vanName}`}>
                        <input
                          className={`${field} min-h-11 max-w-48`}
                          inputMode="decimal"
                          disabled={!i.sendable}
                          type="text"
                          value={
                            edits[`${v.vanId}|${i.itemId}`] ??
                            quantityInput(i.sendHundredths)
                          }
                          onChange={(e) =>
                            setEdits({
                              ...edits,
                              [`${v.vanId}|${i.itemId}`]: e.target.value,
                            })
                          }
                        />
                      </Field>
                      {!i.sendable ? (
                        <p className="text-xs">
                          Currently unavailable for Send; owed quantities remain visible.
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        Ask {formatQty(i.askHundredths)} · Short{' '}
                        {formatQty(i.shortHundredths)} · Waiting{' '}
                        {formatQty(i.waitingHundredths)}
                      </p>
                      {i.legs.map((l) => (
                        <p className="text-xs" key={l.locationId}>
                          {l.sourcePath}: {formatQty(l.quantityHundredths)}
                        </p>
                      ))}
                      {i.incoming.map((p, n) => (
                        <p className="text-xs" key={`${p.purchaseOrderId}:${n}`}>
                          PO-{p.number}
                          {p.status === 'draft' ? ' (draft, not sent)' : ''}:{' '}
                          {formatQty(p.outstandingHundredths)}
                        </p>
                      ))}
                    </div>
                  ))}
                </section>
              ))
          ) : (
            <>
              {data.proposal.buyItems.length === 0 ? (
                <p>No selected Buy lines remain in the current plan.</p>
              ) : null}
              {data.buys
                .filter((b) => b.selected)
                .map((b) => (
                  <section key={b.key} className="space-y-1">
                    <h3 className="font-semibold">
                      {b.name} · {b.vendorName ?? 'No vendor'} → {b.destinationName}
                    </h3>
                    <p>
                      Buy {formatQty(b.buyHundredths)} = For vans{' '}
                      {formatQty(b.forVansHundredths)} + For shelf{' '}
                      {formatQty(b.forShelfHundredths)} + Pack excess{' '}
                      {formatQty(b.packExcessHundredths)}
                    </p>
                    <p className="text-xs">
                      Raw need {formatQty(b.rawBuyHundredths)} · Pack{' '}
                      {formatQty(b.packHundredths)}
                      {b.negativeCorrectionHundredths > 0
                        ? ` · Existing negative shelf correction ${formatQty(b.negativeCorrectionHundredths)}`
                        : ''}
                    </p>
                    {b.vanNeeds.map((v) => (
                      <p className="text-xs" key={v.vanId}>
                        {v.vanName}: remaining {formatQty(v.remainingHundredths)}
                      </p>
                    ))}
                    {b.incoming.map((p, n) => (
                      <p className="text-xs" key={`${p.purchaseOrderId}:${n}`}>
                        PO-{p.number}
                        {p.status === 'draft' ? ' (draft, not sent)' : ''}:{' '}
                        {formatQty(p.outstandingHundredths)}
                      </p>
                    ))}
                    <p className="text-sm">
                      {b.missingCost
                        ? 'Price unknown'
                        : 'unitCostCents' in b && typeof b.unitCostCents === 'number'
                          ? `${money(b.unitCostCents)} each`
                          : 'Price withheld'}
                    </p>
                    {!b.orderable ? (
                      <Notice tone="warn">
                        This part needs an active vendor and stock setup before it can be
                        ordered.
                      </Notice>
                    ) : null}
                  </section>
                ))}
              {'knownSubtotalCents' in data && data.knownSubtotalCents !== undefined ? (
                <p className="text-sm">
                  Known subtotal {money(data.knownSubtotalCents)} ·{' '}
                  {data.unknownCostCount} unknown-price lines. Drafts only; nothing sent.
                </p>
              ) : null}
            </>
          )}
          {data.requiresAcknowledgement ? (
            <Field label="I acknowledge the shop-root stock will become negative">
              <input
                className="min-h-11 min-w-11"
                type="checkbox"
                checked={!!input.acknowledgeNegative}
                onChange={(e) => {
                  setInput({ ...input, acknowledgeNegative: e.target.checked });
                  keys.current = null;
                }}
              />
            </Field>
          ) : null}
          {plan.isError ? (
            <Notice tone="warn">
              Stock or setup could not be reviewed. Your inputs are kept. Refresh before
              submitting.
            </Notice>
          ) : null}
        </div>
      ) : null}
    </Sheet>
  );
}
